import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useExportColumns } from "./hooks/useExportColumns";
import { typedEntries } from "@/lib/utils/typedEntries";
import type { ExportColumnSelectionFeature } from "@/types/exportFeatures";
import { Checkbox } from "@/components/ui/checkbox";

interface ExportDialogColumnSelectionProps {
  feature: ExportColumnSelectionFeature;
  isExportPending: boolean;
  onSelectedColumnsChange: (columns: string[]) => void;
}

export function ExportDialogColumnSelection({
  feature,
  isExportPending,
  onSelectedColumnsChange,
}: ExportDialogColumnSelectionProps) {
  const { t } = useTranslation();
  const { selectedColumns, toggleColumn } = useExportColumns(
    feature.storageKey,
    feature.contextId,
  );

  useEffect(() => {
    onSelectedColumnsChange(selectedColumns);
  }, [selectedColumns, onSelectedColumnsChange]);

  return (
    <div className="mt-6">
      <h3 className="text-sm font-medium mb-2">
        {t("exportDialog.features.columnSelection.section")}
      </h3>
      <p className="text-sm text-muted-foreground mb-3">
        {t("exportDialog.features.columnSelection.description")}
      </p>
      <div className="space-y-2">
        {typedEntries(feature.columns).map(([id, labelKey]) => (
          <label
            key={id}
            htmlFor={`export-column-${id}`}
            className="flex items-center gap-2 cursor-pointer"
          >
            <Checkbox
              id={`export-column-${id}`}
              checked={selectedColumns.includes(id)}
              onCheckedChange={() => toggleColumn(id)}
              disabled={isExportPending}
            />
            <span className="text-sm">{t(labelKey)}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
